import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { cn } from '@/utils/cn';
import { createThemedStyles } from '@/hooks/use-theme-colors';

export type PaymentNoticeTone = 'warning' | 'error' | 'info';

const useTone = createThemedStyles<
  Record<
    PaymentNoticeTone,
    {
      bg: string;
      border: string;
      color: string;
      icon: React.ComponentProps<typeof Ionicons>['name'];
    }
  >
>((th) => ({
  warning: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    color: th('#B45309'),
    icon: 'alert-circle-outline',
  },
  error: {
    bg: 'bg-error-50',
    border: 'border-error-200',
    color: th('#B91C1C'),
    icon: 'warning-outline',
  },
  info: {
    bg: 'bg-info-50',
    border: 'border-info-200',
    color: '#0369A1',
    icon: 'information-circle-outline',
  },
}));

/**
 * Banner NOSSO de aviso em Recebimentos (T6.1, PLANO 6.4). Nunca o
 * componente `notification-banner` do Stripe (WebView — risco em aberto no
 * nativo, ver `TAREFAS-RECEBIMENTO-AUTOMATICO.md`, T6.1): este é 100% app,
 * usa só o que a API já dá (`requirementsDue`/status da conta, notificações)
 * e leva para `payments/activate.tsx` (T4.5), onde a pendência se resolve.
 */
export function PaymentNoticeBanner({
  tone = 'warning',
  title,
  body,
  actionLabel = 'Ver detalhes',
  onPress,
  testID,
}: {
  tone?: PaymentNoticeTone;
  title: string;
  body: string;
  actionLabel?: string;
  onPress: () => void;
  testID?: string;
}) {
  const TONE = useTone();
  const t = TONE[tone];

  return (
    <PressableScale
      haptic
      accessibilityRole="button"
      onPress={onPress}
      testID={testID}
      className={cn('flex-row items-start gap-3 rounded-2xl border p-4', t.bg, t.border)}
      style={{ borderCurve: 'continuous' }}
    >
      <Ionicons name={t.icon} size={20} color={t.color} style={{ marginTop: 1 }} />
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm text-neutral-900">{title}</ThemedText>
        <ThemedText className="text-xs leading-5 text-neutral-600">{body}</ThemedText>
        <ThemedText className="mt-1 font-semibold text-xs text-primary">{actionLabel}</ThemedText>
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
    </PressableScale>
  );
}
